import type { CSSProperties } from 'react'

const SHIMMER_BG = 'linear-gradient(90deg, rgba(26,58,92,0.04) 25%, rgba(26,58,92,0.09) 50%, rgba(26,58,92,0.04) 75%)'
const SHIMMER_ANIMATION = 'shimmer 1.4s ease-in-out infinite'

/**
 * Базовый shimmer-блок. Используй для любых заглушек контента.
 * Keyframe `shimmer` определяется глобально через <SkeletonStyles />,
 * либо уже есть в стилях конкретной страницы (Diary.tsx определяет его).
 */
export function Skeleton({
  width = '100%',
  height = 16,
  borderRadius = 8,
  style,
}: {
  width?: number | string
  height?: number | string
  borderRadius?: number | string
  style?: CSSProperties
}) {
  return (
    <div
      data-testid="skeleton"
      style={{
        width,
        height,
        borderRadius,
        background: SHIMMER_BG,
        backgroundSize: '200% 100%',
        animation: SHIMMER_ANIMATION,
        ...style,
      }}
    />
  )
}

/** Кардочка-плейсхолдер: подходит для блоков отчётов/метрик. */
export function SkeletonCard({
  height = 78,
  style,
}: {
  height?: number | string
  style?: CSSProperties
}) {
  return (
    <Skeleton
      height={height}
      borderRadius={14}
      style={{
        border: '1px dashed rgba(160,174,192,0.20)',
        ...style,
      }}
    />
  )
}

/** Ряд из N одинаковых SkeletonCard'ов в grid'е. */
export function SkeletonRow({
  cols = 2,
  height = 78,
  gap = 8,
}: {
  cols?: number
  height?: number | string
  gap?: number
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap }}>
      {Array.from({ length: cols }).map((_, i) => (
        <SkeletonCard key={i} height={height} />
      ))}
    </div>
  )
}

/** Несколько строк подряд (например для списка лекций или участников). */
export function SkeletonList({
  rows = 4,
  height = 56,
  gap = 8,
}: {
  rows?: number
  height?: number | string
  gap?: number
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonCard key={i} height={height} />
      ))}
    </div>
  )
}

/**
 * Глобальный keyframe `shimmer`. Вставь один раз в App.tsx или layout,
 * чтобы не дублировать <style> в каждой странице.
 */
export function SkeletonStyles() {
  return (
    <style>{`
      @keyframes shimmer {
        0%   { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
  )
}
